/** How the window ended, as it tells the page that asked */
export type Ending = 'proved' | 'failed';

/**
 * How waiting for the window ended: `proved`; `declined` (it failed, was closed or ran out of time); `blocked` (the
 * browser opened no window, so a press of the person's own can open it); `unsafe` (a window opened, but it could not
 * be cut off from this page, so it was closed before going anywhere)
 */
export type Outcome = 'proved' | 'declined' | 'blocked' | 'unsafe';

/** What the page needs of its browser to open the window and hear from it */
interface Scope {
	open(url: string, target: string, features: string): Window | null;
	location: Pick<Location, 'origin'>;
	BroadcastChannel?: typeof BroadcastChannel;
	crypto: Pick<Crypto, 'getRandomValues'>;
	setInterval(handler: () => void, timeout: number): unknown;
	clearInterval(id: unknown): void;
	setTimeout(handler: () => void, timeout: number): unknown;
	clearTimeout(id: unknown): void;
}

/**
 * Signing in again with the account's own provider (GitHub, Google), in a window of this interface, to prove it is
 * still the same person before a sensitive action (E8, re-authentication in place). It is for an account with no
 * password and no second factor, which has nothing to prove in place otherwise.
 *
 * The window (`/signin/again`) makes the provider's round trip and gives the provider's token to
 * `POST /v1/sessions/current/reauthentication` with the method `provider`. The service accepts it only for this
 * account's own identity and a recent provider sign-in. It renews **this** session, so the page that asked keeps
 * everything the person was doing (a connection's request, a form) and sends it once more itself.
 *
 * As the family's session window does (`Trip` of `@beyond-js/ui`), it opens blank and cuts the window off from this
 * page before it goes anywhere: when the browser keeps `opener` set, the blank window is closed and nothing is
 * navigated (`unsafe`). The window answers on this origin's `beyond-accounts-again` channel with the attempt it was
 * opened for, so an answer meant for another page or another attempt is ignored.
 *
 * A window this page sees closed ends the wait as `declined` after a grace. A provider's page can make the browser
 * report the window closed while it is still open (a cross-origin opener policy swaps its browsing context), so the
 * channel is still heard until the bound: a proof that arrives later is given to `late`, and the page goes on only when
 * the person presses again. Nothing here is taken as proof by the service, which checks the session's time again.
 */
export class Again {
	static readonly PATH = '/signin/again';
	static readonly CHANNEL = 'beyond-accounts-again';
	/** How long the page waits for the window: a connection's request lives ten minutes */
	static readonly WAIT = 10 * 60 * 1000;
	/** How long a window seen closed is given for its last answer to arrive */
	static readonly #GRACE = 1500;
	static readonly #POLL = 500;

	#scope: Scope;

	constructor(scope: Scope = globalThis as unknown as Scope) {
		this.#scope = scope;
	}

	/**
	 * Opens the window and waits for how it ends. Call it from the person's press with nothing awaited before it, or
	 * the browser blocks it.
	 *
	 * @param late Called once when the window proves it after the wait had already ended as `declined`
	 */
	prove(late?: () => void): Promise<Outcome> {
		const scope = this.#scope;
		if (typeof scope.BroadcastChannel !== 'function') return Promise.resolve('unsafe');
		const attempt = Again.#attempt(scope);
		const opened = scope.open('', Again.CHANNEL, 'popup,width=520,height=680');
		if (!opened) return Promise.resolve('blocked');
		if (!Again.#cut(opened)) return Promise.resolve('unsafe');
		try {
			opened.location.replace(`${scope.location.origin}${Again.PATH}?attempt=${attempt}`);
		} catch {
			Again.#close(opened);
			return Promise.resolve('unsafe');
		}
		const channel = new scope.BroadcastChannel(Again.CHANNEL);
		return new Promise(resolve => {
			let polling: unknown;
			let limit: unknown;
			let grace: unknown;
			let waiting = true;
			const stop = () => {
				channel.close();
				scope.clearInterval(polling);
				scope.clearTimeout(limit);
				scope.clearTimeout(grace);
			};
			channel.onmessage = (event: MessageEvent) => {
				const data = event.data as { attempt?: unknown; outcome?: unknown } | null;
				if (data?.attempt !== attempt) return;
				const proved = data.outcome === 'proved';
				stop();
				if (waiting) resolve(proved ? 'proved' : 'declined');
				else if (proved) late?.();
				waiting = false;
			};
			polling = scope.setInterval(() => {
				if (!opened.closed || grace) return;
				grace = scope.setTimeout(() => {
					waiting = false;
					scope.clearInterval(polling);
					resolve('declined');
				}, Again.#GRACE);
			}, Again.#POLL);
			limit = scope.setTimeout(() => {
				stop();
				if (waiting) resolve('declined');
				waiting = false;
			}, Again.WAIT);
		});
	}

	/**
	 * In the window: tells the page that asked how it ended, on this origin's channel, for the attempt in its address.
	 * Nothing here is a credential: the page goes on only because the service answers it.
	 */
	static tell(outcome: Ending, scope: Pick<Scope, 'BroadcastChannel'> & { location: Pick<Location, 'search'> } = globalThis as never): void {
		const attempt = new URLSearchParams(scope.location.search).get('attempt');
		if (!attempt || typeof scope.BroadcastChannel !== 'function') return;
		const channel = new scope.BroadcastChannel(Again.CHANNEL);
		channel.postMessage({ attempt, outcome });
		channel.close();
	}

	/** Whether a fresh sign-in is the only way these methods leave to prove it, so the window is what a press opens */
	static only(methods: string[], passkeys: boolean): boolean {
		return methods.includes('provider') && !methods.includes('password') && !methods.includes('totp') && !(passkeys && methods.includes('passkey'));
	}

	/** The address of the provider's return: this window again, with its attempt */
	static back(scope: { location: Pick<Location, 'origin' | 'search'> } = globalThis as never): string {
		const attempt = new URLSearchParams(scope.location.search).get('attempt') ?? '';
		return `${scope.location.origin}${Again.PATH}?attempt=${encodeURIComponent(attempt)}`;
	}

	/** Cuts the blank window off from this page; when the browser keeps the link, closes it and says so */
	static #cut(opened: Window): boolean {
		try {
			opened.opener = null;
		} catch {
			// Read below: what matters is whether the link is gone
		}
		let kept: unknown;
		try {
			kept = opened.opener;
		} catch {
			kept = true;
		}
		if (kept === null || kept === undefined) return true;
		Again.#close(opened);
		return false;
	}

	static #close(opened: Window): void {
		try {
			opened.close();
		} catch {
			// A window that cannot be closed from here is still blank: it was never sent anywhere
		}
	}

	static #attempt(scope: Scope): string {
		const bytes = scope.crypto.getRandomValues(new Uint8Array(16));
		return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
	}
}
